import { PropsWithChildren, useEffect, useState } from 'react';
import LoadingScreen from '~/components/LoadingScreen';
import { useActiveCompanyId, useInternalSwitchCompany, useSwitchCompany } from '~/hooks/useActiveCompany';
import RedirectWithQueryParams from '~/routes/RedirectWithQueryParams';
import { RouteConfig } from '~/routes/routeConfig';
import { useLoggedUser } from '~/services/user';

export const CompanyGuard = ({ children }: PropsWithChildren) => {
  const state = useEnsureInitialCompany();

  switch (state) {
    case 'loading':
      return <LoadingScreen />;
    case 'no_company':
      return <RedirectWithQueryParams path={RouteConfig.SignUpAddCompany.buildLink()} />;
    default:
      return children;
  }
};

const useEnsureInitialCompany = () => {
  const companyId = useActiveCompanyId();
  const switchCompany = useSwitchCompany();
  const internalSwitchCompany = useInternalSwitchCompany();
  const [loading, setLoaded] = useState<'loading' | 'loaded' | 'no_company'>('loading');
  const { data: user } = useLoggedUser();

  useEffect(() => {
    const companies = user?.companies;
    if (!companies) return;
    if (companies.length < 1) {
      setLoaded('no_company');
      return;
    }

    const founded = companies.find((x) => x.id === companyId);
    if (!founded) {
      // Set the active company synchronously so the next render already has a valid
      // companyId. Going through CompanyRedirect here would create a loop if the
      // company fetch fails (401, transient error): the trampoline would navigate back
      // without setting the atom and we'd bounce forever.
      internalSwitchCompany(companies[0]!);
      return;
    }
    switchCompany(founded, RouteConfig.AppRoot.buildLink());
    setLoaded('loaded');
  }, [user?.companies, companyId, switchCompany, internalSwitchCompany]);

  return loading;
};
